<script>
import FiltersPanel from "@/components/filters/FiltersPanel.vue";
import PerPageSelect from "@/components/ui/PerPageSelect.vue";
import {useVisibility} from "@/composables/useVisibility.js";

export default {
    components: {FiltersPanel, PerPageSelect},
    setup() {
        const {showTax, showAutoSettlement} = useVisibility();
        return {showTax, showAutoSettlement};
    },
    data() {
        return {
            training: null,
            loadingTraining: true,
            loading: true,
            sending: false,
            downloading: false,
            users: [],
            selectedIds: [],
            meta: {
                current_page: 1,
                total: 0,
                per_page: 0,
            },
            sort: {
                field: 'last_name',
                order: 'asc',
            },
            controller: null,
            filters: {},
            perPage: parseInt(localStorage.getItem('perPage_employees') || '10'),
        };
    },
    computed: {
        trainingId() {
            return this.$route.params.id;
        },
        filtersDef() {
            return [
                {key: 'first_name', label: this.$t('users.fields.first_name.name'), type: 'text'},
                {key: 'last_name', label: this.$t('users.fields.last_name.name'), type: 'text'},
                {key: 'company.name', label: this.$t('users.fields.company.name'), type: 'text'},
                {key: 'pesel', label: this.$t('users.fields.pesel.name'), type: 'text'},
                {key: 'phone_number', label: this.$t('users.fields.phone_number.name'), type: 'text'},
                {key: 'email', label: this.$t('users.fields.email.name'), type: 'text'},
                {key: 'bank_account_number', label: this.$t('users.fields.bank_account_number.name'), type: 'text'},
                {
                    key: 'inactive_with_tokens',
                    label: this.$t('users.labels.inactive_accounts_filter'),
                    type: 'text',
                    placeholder: '30',
                    tooltip: this.$t('users.labels.inactive_accounts_filter_info'),
                },
                {
                    key: 'active_with_tokens',
                    label: this.$t('users.labels.active_accounts_filter'),
                    type: 'text',
                    placeholder: '30',
                    tooltip: this.$t('users.labels.active_accounts_filter_info'),
                },
                {
                    key: 'no_trainings_tokens',
                    label: this.$t('users.labels.no_trainings_tokens_filter'),
                    type: 'text',
                    placeholder: '30',
                    tooltip: this.$t('users.labels.no_trainings_tokens_filter_info'),
                },
                {
                    key: 'trainings_tokens',
                    label: this.$t('users.labels.trainings_tokens_filter'),
                    type: 'text',
                    placeholder: '30',
                    tooltip: this.$t('users.labels.trainings_tokens_filter_info'),
                },
                {key: 'token_balance', label: this.$t('users.fields.token_balance.name'), type: 'number'},
                {key: 'without_company', label: this.$t('users.labels.without_company_filter'), type: 'checkbox'},
            ];
        },
        isAllSelected() {
            return this.users.length > 0 && this.users.every(u => this.selectedIds.includes(u.id));
        },
        noneSelected() {
            return this.selectedIds.length === 0;
        },
    },
    methods: {
        onFiltersChange(filters) {
            this.filters = filters;
            this.meta.current_page = 1;
            this.loadData();
        },
        loadData() {
            if (this.controller) this.controller.abort();
            this.controller = new AbortController();
            this.loading = true;
            window.scrollTo({top: 0});
            return axios.get('/users/employees', {
                signal: this.controller.signal,
                params: {
                    page: this.meta.current_page,
                    per_page: this.perPage,
                    sort: this.sort.field,
                    order: this.sort.order,
                    filters: this.filters,
                    except_training_id: this.trainingId,
                },
            }).then(response => {
                this.users = response.data.data;
                this.meta.current_page = response.data.meta.current_page;
                this.meta.total = response.data.meta.total;
                this.meta.per_page = response.data.meta.per_page;
            }).catch(() => {
            }).finally(() => {
                this.loading = false;
            });
        },
        loadTraining() {
            this.loadingTraining = true;
            axios.get('/trainings/' + this.trainingId).then(response => {
                this.training = response.data.data;
            }).finally(() => {
                this.loadingTraining = false;
            });
        },
        onPageChange(page) {
            this.meta.current_page = page;
            this.loadData();
        },
        onSort(field, order) {
            this.sort = {field, order};
            this.loadData();
        },
        onPerPageChange() {
            localStorage.setItem('perPage_employees', String(this.perPage));
            this.meta.current_page = 1;
            this.loadData();
        },
        toggleSelectAll() {
            const pageIds = this.users.map(u => u.id);
            if (this.isAllSelected) {
                this.selectedIds = this.selectedIds.filter(id => !pageIds.includes(id));
            } else {
                const toAdd = pageIds.filter(id => !this.selectedIds.includes(id));
                this.selectedIds = [...this.selectedIds, ...toAdd];
            }
        },
        sendInvites() {
            this.sending = true;
            axios.post('/trainings/' + this.trainingId + '/send-invites', {
                user_ids: this.selectedIds,
            }).then(() => {
                this.$toast.success(this.$t('training_enrollments.messages.invitations_sent'));
                this.selectedIds = [];
                this.loadData();
            }).catch(error => {
                if (error.response?.data?.error) {
                    this.$toast.open({message: error.response.data.error, type: 'error'});
                }
            }).finally(() => {
                this.sending = false;
            });
        },
        downloadLinks() {
            this.downloading = true;
            axios({
                url: '/trainings/' + this.trainingId + '/invite-links',
                method: 'POST',
                responseType: 'blob',
                data: {user_ids: this.selectedIds},
            }).then(response => {
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const a = document.createElement('a');
                a.href = url;
                a.download = 'linki-zaproszenia.csv';
                document.body.appendChild(a);
                a.click();
                document.body.removeChild(a);
                window.URL.revokeObjectURL(url);
            }).catch(() => {
                this.$toast.open({message: this.$t('messages.error'), type: 'error'});
            }).finally(() => {
                this.downloading = false;
            });
        },
    },
    mounted() {
        this.loadTraining();
        this.loadData();
    },
    beforeUnmount() {
        this.controller && this.controller.abort();
    },
};
</script>

<template>
    <div>
        <div class="mb-4" v-if="training">
            <p class="title is-5 mb-1">{{ training.name }}</p>
            <p class="has-text-grey">{{ $t('trainings.fields.price.name') }}: {{ training.price }}</p>
        </div>

        <div class="mb-4">
            <FiltersPanel
                :filters="filtersDef"
                @update:activeFilters="onFiltersChange"
            />
        </div>

        <div class="is-flex is-justify-content-space-between is-align-items-center mb-3">
            <div class="is-flex is-align-items-center">
                <b-button size="is-small" @click="toggleSelectAll" :disabled="loading || users.length === 0">
                    {{ isAllSelected ? $t('training_enrollments.labels.deselect_all') : $t('training_enrollments.labels.select_all') }}
                </b-button>
                <div class="has-text-grey is-size-7 ml-2" v-if="selectedIds.length > 0">
                    {{ $t('training_enrollments.labels.selected') }}: {{ selectedIds.length }}
                </div>
            </div>
            <div class="buttons">
                <b-button
                    type="is-primary"
                    :disabled="noneSelected || sending"
                    :loading="sending"
                    icon-left="send"
                    @click="sendInvites"
                >
                    {{ $t('actions.send') }} ({{ selectedIds.length }})
                </b-button>
                <b-button
                    v-if="$auth.isAdmin()"
                    type="is-link"
                    :disabled="noneSelected || downloading"
                    :loading="downloading"
                    icon-left="download"
                    @click="downloadLinks"
                >
                    {{ $t('training_enrollments.labels.download_links') }} ({{ selectedIds.length }})
                </b-button>
            </div>
        </div>

        <b-table
            :data="loading ? [] : users"
            :loading="loading"
            detailed
            :show-detail-icon="true"
            detail-icon="chevron-right"

            :paginated="!loading"
            backend-pagination
            :total="loading ? 0 : meta.total"
            :per-page="perPage"
            @page-change="onPageChange"
            :aria-next-label="$t('navigation.next_page')"
            :aria-previous-label="$t('navigation.previous_page')"
            :aria-page-label="$t('navigation.page')"
            :aria-current-label="$t('navigation.current_page')"

            backend-sorting
            sort-icon="arrow-up"
            default-sort-direction="asc"
            :default-sort="['last_name', 'asc']"
            @sort="onSort"
        >
            <b-table-column field="select" label="" width="40" v-slot="props">
                <b-checkbox
                    :native-value="props.row.id"
                    v-model="selectedIds"
                />
            </b-table-column>

            <b-table-column field="first_name" :label="$t('users.fields.first_name.name')" sortable v-slot="props">
                {{ props.row.first_name }}
            </b-table-column>

            <b-table-column field="last_name" :label="$t('users.fields.last_name.name')" sortable v-slot="props">
                {{ props.row.last_name }}
            </b-table-column>

            <b-table-column field="company.name" :label="$t('users.fields.company.name')" v-if="$auth.isAdmin()" v-slot="props">
                <div v-for="company in props.row.companies" :key="company.id">{{ company.name }}</div>
            </b-table-column>

            <b-table-column field="email" label="E-mail" v-slot="props">
                {{ props.row.email }}
            </b-table-column>

            <b-table-column field="token_balance" :label="$t('users.fields.token_balance.name')" sortable
                            v-slot="props">
                {{ props.row.token_balance }}
            </b-table-column>

            <template #empty>
                <div v-if="!loading" class="block has-text-grey has-text-centered mb-4 mt-4">
                    {{ $t('employees.labels.no_employees') }}
                </div>
            </template>

            <template #detail="props">
                <article class="media">
                    <div class="media-content">
                        <div class="content">
                            <p><strong>{{ props.row.first_name }} {{ props.row.last_name }}</strong></p>
                            <p><b-icon icon="at"/> {{ props.row.email }}</p>
                            <p v-if="props.row.phone_number"><b-icon icon="phone"/> {{ props.row.phone_number }}</p>
                        </div>
                    </div>
                </article>
            </template>

            <template #bottom-left>
                <PerPageSelect v-model="perPage" @change="onPerPageChange"/>
            </template>
        </b-table>
    </div>
</template>
